import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faBuilding, faFilter, faCalendarAlt, faDollarSign, faChartLine } from '@fortawesome/free-solid-svg-icons';
import { marketDataApi } from '../api/marketDataApi';

const UpcomingCorporateEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('2'); // weeks
  const [selectedTypes, setSelectedTypes] = useState(new Set(['cash_dividends', 'forward_split', 'reverse_split']));
  const [allSymbols, setAllSymbols] = useState([]);

  // Get symbols from positions and watchlist
  useEffect(() => {
    const fetchSymbols = async () => {
      try {
        // Get positions
        const { accountApi } = await import('../api/accountApi');
        const positionsRes = await accountApi.getPositions();
        const positionSymbols = positionsRes.data?.map(p => p.symbol) || [];

        // Get watchlist symbols (if available)
        // You might need to adjust this based on your watchlist API
        const symbols = [...new Set(positionSymbols)];
        
        if (symbols.length > 0) {
          setAllSymbols(symbols);
        } else {
          // If no positions, use some default symbols for demo
          setAllSymbols(['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'TSLA']);
        }
      } catch (err) {
        console.error('Error fetching symbols:', err);
        // Fallback to default symbols
        setAllSymbols(['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'TSLA']);
      }
    };

    fetchSymbols();
  }, []);

  useEffect(() => {
    if (allSymbols.length > 0) {
      loadEvents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allSymbols, timeRange, selectedTypes]);

  const loadEvents = async () => {
    if (allSymbols.length === 0) return;

    try {
      setLoading(true);
      setError(null);

      const startDate = new Date();
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + parseInt(timeRange, 10) * 7);

      // One batched Alpaca call instead of N sequential (N was timing out)
      const symbolsToCheck = allSymbols.slice(0, 20);
      const typesParam = [...selectedTypes]
        .map((t) => {
          if (t === 'cash_dividends') return 'cash_dividend';
          if (t === 'stock_dividends') return 'stock_dividend';
          return t;
        })
        .join(',');

      const response = await marketDataApi.getCorporateActions(symbolsToCheck, {
        start: startDate.toISOString().split('T')[0],
        end: endDate.toISOString().split('T')[0],
        types: typesParam,
      });

      const allEvents = Array.isArray(response?.data) ? response.data : [];

      const typeAliases = {
        cash_dividend: 'cash_dividends',
        stock_dividend: 'stock_dividends',
      };
      const filteredEvents = allEvents
        .filter((event) => {
          const eventType = event.type || 'unknown';
          if (selectedTypes.has(eventType)) return true;
          const aliased = typeAliases[eventType];
          return aliased ? selectedTypes.has(aliased) : false;
        })
        .filter((event) => {
          const eventDate = event.processDate || event.exDate || event.date;
          if (!eventDate) return false;
          const eventDateObj = new Date(eventDate);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return eventDateObj >= today;
        })
        .sort((a, b) => {
          const dateA = new Date(a.processDate || a.exDate || a.date || 0);
          const dateB = new Date(b.processDate || b.exDate || b.date || 0);
          return dateA - dateB; // Sort ascending (earliest first)
        });

      setEvents(filteredEvents);
    } catch (err) {
      console.error('Error loading corporate events:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load corporate events');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleType = (type) => {
    const newTypes = new Set(selectedTypes);
    if (newTypes.has(type)) {
      newTypes.delete(type);
    } else {
      newTypes.add(type);
    }
    setSelectedTypes(newTypes);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const getTypeLabel = (type) => {
    const labels = {
      cash_dividends: 'Cash Dividend',
      stock_dividends: 'Stock Dividend',
      forward_split: 'Forward Split',
      reverse_split: 'Reverse Split',
      unit_split: 'Unit Split',
      spin_off: 'Spin-off',
      cash_merger: 'Cash Merger',
      stock_merger: 'Stock Merger',
      stock_and_cash_merger: 'Stock & Cash Merger',
      redemption: 'Redemption',
      name_change: 'Name Change',
      worthless_removal: 'Worthless Removal',
      rights_distribution: 'Rights Distribution',
    };
    return labels[type] || type;
  };

  const getTypeColor = (type) => {
    if (type.includes('dividend')) return 'text-green-600 bg-green-50';
    if (type.includes('split')) return 'text-blue-600 bg-blue-50';
    if (type.includes('merger')) return 'text-purple-600 bg-purple-50';
    return 'text-gray-600 bg-gray-50';
  };

  const eventTypes = [
    'cash_dividends',
    'stock_dividends',
    'forward_split',
    'reverse_split',
    'unit_split',
    'spin_off',
    'cash_merger',
    'stock_merger',
  ];

  return (
    <div className="bg-white rounded-lg shadow-md p-3 border border-gray-100">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-bold text-gray-800 flex items-center">
          <FontAwesomeIcon icon={faBuilding} className="mr-2 text-indigo-600 text-sm" />
          Upcoming Corporate Events
        </h2>
        <div className="flex items-center space-x-2">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-2 py-1 border border-gray-300 rounded-md text-xs focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="2">2 Weeks</option>
            <option value="4">4 Weeks</option>
          </select>
          {loading && (
            <FontAwesomeIcon icon={faSpinner} className="animate-spin text-gray-400 text-xs" />
          )}
        </div>
      </div>

      {/* Type Filters */}
      <div className="mb-3 flex flex-wrap gap-1.5">
        {eventTypes.map(type => (
          <button
            key={type}
            onClick={() => toggleType(type)}
            className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
              selectedTypes.has(type)
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {getTypeLabel(type)}
          </button>
        ))}
      </div>

      {error ? (
        <div className="text-xs text-red-600 py-2">{error}</div>
      ) : loading ? (
        <div className="text-center py-4 text-gray-500 text-xs">
          <FontAwesomeIcon icon={faSpinner} className="animate-spin text-sm mb-1" />
          <p>Loading events...</p>
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-4 text-gray-500 text-xs">
          No upcoming corporate events found
        </div>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {events.map((event, index) => (
            <div
              key={`${event.id || index}-${event.symbol}`}
              className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200 hover:bg-gray-100 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-1">
                  <span className="font-semibold text-xs text-gray-900">{event.symbol}</span>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getTypeColor(event.type)}`}>
                    {getTypeLabel(event.type)}
                  </span>
                </div>
                <div className="flex items-center space-x-3 text-[10px] text-gray-600">
                  {event.rate && (
                    <span className="flex items-center">
                      <FontAwesomeIcon icon={faDollarSign} className="mr-0.5" />
                      {event.rate.toFixed(4)}
                    </span>
                  )}
                  {(event.oldRate || event.newRate) && (
                    <span>
                      {event.oldRate}:{event.newRate}
                    </span>
                  )}
                  <span className="flex items-center">
                    <FontAwesomeIcon icon={faCalendarAlt} className="mr-0.5" />
                    {formatDate(event.processDate || event.exDate)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UpcomingCorporateEvents;

